import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  makeStyles,
} from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import CloseIcon from '@material-ui/icons/Close';
import BrightnessAutoIcon from '@material-ui/icons/BrightnessAuto';
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import LanguageIcon from '@material-ui/icons/Language';
import PaletteIcon from '@material-ui/icons/PaletteOutlined';
import IconButton from '@material-ui/core/IconButton';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslation } from 'react-i18next';
import { useDialogStyles } from './dialogStyles';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { queueLanguageChange } from '../../i18n/languageChangeQueue';

export type ThemeMode = 'auto' | 'light' | 'dark';

type SettingsDialogProps = {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  onThemePreview?: (themeMode: ThemeMode) => void;
  /**
   * Called to clear any live theme preview immediately - on Cancel/Save, and
   * when the dialog (re)opens. Relying only on the Dialog's `onExited` (fired
   * after its close transition finishes) leaves a stale preview in place if
   * the dialog is reopened quickly enough to interrupt that transition,
   * since `onExited` then never fires for the interrupted close.
   */
  onThemePreviewReset?: () => void;
};

const themeIcons = {
  auto: BrightnessAutoIcon,
  light: Brightness7Icon,
  dark: Brightness4Icon,
};

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
    '&:last-child': { marginBottom: 0 },
  },
  divider: {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(0, 0, 3),
  },
  optionGroup: {
    display: 'flex',
    width: 'fit-content',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 1.5,
    overflow: 'hidden',
    '& .MuiToggleButtonGroup-grouped': {
      border: 'none',
      borderRadius: 0,
      '&:not(:last-child)': {
        borderRight: `1px solid ${theme.palette.divider}`,
      },
    },
  },
  option: {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 96,
    padding: theme.spacing(0.75, 1.5),
    justifyContent: 'center',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  optionIcon: {
    flexShrink: 0,
    fontSize: '1.05rem',
    marginLeft: theme.spacing(1.25),
  },
  category: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: '0.9rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  categoryIcon: { fontSize: '1.1rem' },
  languageSwitcher: {
    width: '100%',
    maxWidth: 'none',
  },
}));

export function SettingsDialog({
  open,
  onClose,
  onExited,
  onThemePreview,
  onThemePreviewReset,
}: SettingsDialogProps) {
  const classes = useDialogStyles();
  const settingsClasses = useStyles();
  const appThemeApi = useApi(appThemeApiRef);
  const { t, i18n } = useTranslation('shell');
  const [themeMode, setThemeMode] = useState<ThemeMode>('auto');
  const [originalLanguage, setOriginalLanguage] = useState(i18n.language);

  useEffect(() => {
    if (!open) {
      return;
    }

    const activeThemeId = appThemeApi.getActiveThemeId();
    const nextThemeMode =
      activeThemeId === 'light' || activeThemeId === 'dark'
        ? activeThemeId
        : 'auto';
    setThemeMode(nextThemeMode);
    setOriginalLanguage(i18n.language);
    // Clears any preview left over from a previous open that was interrupted
    // before its close transition reached onExited (see onThemePreviewReset).
    onThemePreviewReset?.();
    // onThemePreviewReset is intentionally omitted: it is not expected to
    // change identity in a way that should re-run this reset, and including
    // it would re-fire the reset on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appThemeApi, i18n, open]);

  const handleThemeChange = (nextThemeMode: ThemeMode) => {
    setThemeMode(nextThemeMode);
    onThemePreview?.(nextThemeMode);
  };

  const handleCancel = () => {
    void queueLanguageChange(originalLanguage);
    onThemePreviewReset?.();
    onClose();
  };

  const handleSave = () => {
    // 'auto' has no corresponding registered AppTheme id (see App.tsx) -
    // passing undefined clears the explicit selection, which is what makes
    // Backstage's own AppThemeProvider follow the OS preference.
    appThemeApi.setActiveThemeId(themeMode === 'auto' ? undefined : themeMode);
    onThemePreviewReset?.();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <span className={classes.titleTextWrap}>
          <span className={classes.titleText}>{t('settings')}</span>
          <span className={classes.titleSub}>{t('settingsDescription')}</span>
        </span>
        <IconButton
          size="small"
          className={classes.closeBtn}
          aria-label={t('closeSettings')}
          onClick={handleCancel}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.body}>
        <div className={settingsClasses.category}>
          <PaletteIcon
            className={settingsClasses.categoryIcon}
            color="secondary"
            aria-hidden="true"
          />
          <span>{t('appearance')}</span>
        </div>

        <FormControl
          component="fieldset"
          fullWidth
          className={settingsClasses.section}
        >
          <span className={classes.label}>{t('theme')}</span>
          <ToggleButtonGroup
            exclusive
            size="small"
            className={settingsClasses.optionGroup}
            aria-label={t('theme')}
            value={themeMode}
            onChange={(_, nextThemeMode: ThemeMode | null) => {
              if (nextThemeMode) {
                handleThemeChange(nextThemeMode);
              }
            }}
          >
            {(['auto', 'light', 'dark'] as ThemeMode[]).map(mode => {
              const Icon = themeIcons[mode];
              return (
                <ToggleButton
                  key={mode}
                  value={mode}
                  className={settingsClasses.option}
                >
                  <span>{t(`themeOptions.${mode}`)}</span>
                  <Icon
                    className={settingsClasses.optionIcon}
                    aria-hidden="true"
                  />
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </FormControl>

        <hr className={settingsClasses.divider} />

        <div className={settingsClasses.category}>
          <LanguageIcon
            className={settingsClasses.categoryIcon}
            color="secondary"
            aria-hidden="true"
          />
          <span>{t('languageRegion')}</span>
        </div>

        <div className={settingsClasses.section}>
          <LanguageSwitcher
            className={settingsClasses.languageSwitcher}
            showChangeNotification={false}
          />
        </div>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={handleCancel}>{t('cancel')}</Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          {t('saveSettings')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
